<template>
  <div class="rewards-page">
    <b-row>
      <b-col cols="12" md="6" lg="8">
        <h2 class="my-2">{{ $t('rewards.title') }} <font-awesome-icon class="ml-1" icon="store"/></h2>
        <p class="my-2" v-if="isRecruiter">{{ $t('rewards.description-recruiter') }}</p>
        <p class="my-2" v-else>{{ $t('rewards.description-employee') }}</p>
      </b-col>
      <b-col cols="12" md="6" lg="4" class="d-flex flex-row justify-content-end align-items-start pt-2" v-if="isRecruiterAdmin">
        <b-btn class="mr-1" @click.prevent="() => selectedEditID = 'NEW'" variant="outline-primary">
          {{ $t('rewards.ctas.create-reward') }}
        </b-btn>
        <b-btn class="mr-1" variant="outline-primary" v-if="isRecruiterAdmin" v-b-modal.recruiterList>
          {{ $t('rewards.ctas.manage-claims') }} ({{ Array.isArray(manageClaimedRewards) ? manageClaimedRewards.length : 0 }})
        </b-btn>
      </b-col>
    </b-row>

    <b-card class="d-block d-sm-none my-3" body-class="text-center">
      <b-card-title><strong>{{ $t('rewards.balance-title') }}</strong></b-card-title>
      <b-card-text>{{ rewardTokensBalance }} <rewards-coin/></b-card-text>
    </b-card>

    <h3 v-if="rewardScheme">{{ $t('rewards.how-you-earn-tokens-title') }}</h3>

    <b-card-group class="my-3" v-if="rewardScheme">
      <b-card v-if="rewardScheme.CONTACT_NETWORK_SYNCED" body-class="text-center">
        <b-card-title><strong>{{ $t('rewards.events.CONTACT_NETWORK_SYNCED') }}</strong></b-card-title>
        <b-card-text>{{ rewardScheme.CONTACT_NETWORK_SYNCED }} <rewards-coin/></b-card-text>
      </b-card>
      <b-card v-if="rewardScheme.SHARE_FIRST_VISITED" body-class="text-center">
        <b-card-title><strong>{{ $t('rewards.events.SHARE_FIRST_VISITED') }}</strong></b-card-title>
        <b-card-text>{{ rewardScheme.SHARE_FIRST_VISITED }} <rewards-coin/></b-card-text>
      </b-card>
      <b-card v-if="rewardScheme.REFERRAL_MATCH_CREATED" body-class="text-center">
        <b-card-title><strong>{{ $t('rewards.events.REFERRAL_MATCH_CREATED') }}</strong></b-card-title>
        <b-card-text>{{ rewardScheme.REFERRAL_MATCH_CREATED }} <rewards-coin/></b-card-text>
      </b-card>
      <b-card v-if="rewardScheme.APPLICATION_CREATED" body-class="text-center">
        <b-card-title><strong>{{ $t('rewards.events.APPLICATION_CREATED') }}</strong></b-card-title>
        <b-card-text>{{ rewardScheme.APPLICATION_CREATED }} <rewards-coin/></b-card-text>
      </b-card>
      <b-card v-if="rewardScheme.APPLICATION_CONFIRMED" body-class="text-center">
        <b-card-title><strong>{{ $t('rewards.events.APPLICATION_CONFIRMED') }}</strong></b-card-title>
        <b-card-text>{{ rewardScheme.APPLICATION_CONFIRMED }} <rewards-coin/></b-card-text>
      </b-card>
      <b-card v-if="rewardScheme.APPLICATION_INTERVIEW" body-class="text-center">
        <b-card-title><strong>{{ $t('rewards.events.APPLICATION_INTERVIEW') }}</strong></b-card-title>
        <b-card-text>{{ rewardScheme.APPLICATION_INTERVIEW }} <rewards-coin/></b-card-text>
      </b-card>
      <b-card v-if="rewardScheme.APPLICATION_HIRED" body-class="text-center">
        <b-card-title><strong>{{ $t('rewards.events.APPLICATION_HIRED') }}</strong></b-card-title>
        <b-card-text>{{ rewardScheme.APPLICATION_HIRED }} <rewards-coin/></b-card-text>
      </b-card>
    </b-card-group>

    <template v-if="canClaimRewards && rewardScheme">
      <h3>{{ $t('rewards.your-tokens-title') }}</h3>
      <b-card class="table-responsive my-3" no-body>
        <table class="table b-table m-0">
          <thead>
            <tr>
              <th v-if="rewardScheme.CONTACT_NETWORK_SYNCED">{{ $t('rewards.events.CONTACT_NETWORK_SYNCED') }}</th>
              <th v-if="rewardScheme.SHARE_FIRST_VISITED">{{ $t('rewards.events.SHARE_FIRST_VISITED') }}</th>
              <th v-if="rewardScheme.REFERRAL_MATCH_CREATED">{{ $t('rewards.events.REFERRAL_MATCH_CREATED') }}</th>
              <th v-if="rewardScheme.APPLICATION_CREATED">{{ $t('rewards.events.APPLICATION_CREATED') }}</th>
              <th v-if="rewardScheme.APPLICATION_CONFIRMED">{{ $t('rewards.events.APPLICATION_CONFIRMED') }}</th>
              <th v-if="rewardScheme.APPLICATION_INTERVIEW">{{ $t('rewards.events.APPLICATION_INTERVIEW') }}</th>
              <th v-if="rewardScheme.APPLICATION_HIRED">{{ $t('rewards.events.APPLICATION_HIRED') }}</th>
              <th class="border-left">{{ $t('rewards.labels.totalEarned') }}</th>
              <th>{{ $t('rewards.labels.totalBalance') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td v-if="rewardScheme.CONTACT_NETWORK_SYNCED">{{ rewardSchemeCounts.CONTACT_NETWORK_SYNCED || 0 }}</td>
              <td v-if="rewardScheme.SHARE_FIRST_VISITED">{{ rewardSchemeCounts.SHARE_FIRST_VISITED || 0 }}</td>
              <td v-if="rewardScheme.REFERRAL_MATCH_CREATED">{{ rewardSchemeCounts.REFERRAL_MATCH_CREATED || 0 }}</td>
              <td v-if="rewardScheme.APPLICATION_CREATED">{{ rewardSchemeCounts.APPLICATION_CREATED || 0 }}</td>
              <td v-if="rewardScheme.APPLICATION_CONFIRMED">{{ rewardSchemeCounts.APPLICATION_CONFIRMED || 0 }}</td>
              <td v-if="rewardScheme.APPLICATION_INTERVIEW">{{ rewardSchemeCounts.APPLICATION_INTERVIEW || 0 }}</td>
              <td v-if="rewardScheme.APPLICATION_HIRED">{{ rewardSchemeCounts.APPLICATION_HIRED || 0 }}</td>
              <td class="border-left">{{ rewardTokensTotal }} <rewards-coin/></td>
              <td>{{ rewardTokensBalance }} <rewards-coin/></td>
            </tr>
          </tbody>
        </table>
      </b-card>
    </template>

    <template v-if="canClaimRewards && Array.isArray(beforeClaimedRewards) && beforeClaimedRewards.length">
      <h3>{{ $t('rewards.claimed-rewards-title') }}</h3>
      <b-row class="mt-3">
        <b-col class="mb-3" cols="12" sm="6" v-for="entry in beforeClaimedRewards" :key="`reward-${entry.id}`">
          <b-card class="h-100">
            <div class="d-flex flex-row justify-content-start align-items-center mb-3">
              <span v-if="entry.reward">{{ entry.reward.title }}</span>
              <div class="flex-fill"/>
              <strong v-if="entry.status === 'REQUESTED' || entry.status === 'VERIFIED'">{{ $t('rewards.statuses.REQUESTED') }}</strong>
              <strong v-if="entry.status === 'APPROVED'">{{ $t('rewards.statuses.APPROVED') }}</strong>
              <strong v-if="entry.status === 'FULFILLED'">{{ $t('rewards.statuses.FULFILLED') }}</strong>
              <strong v-if="entry.status === 'REJECTED'">{{ $t('rewards.statuses.REJECTED') }}</strong>
            </div>
            <dl class="row text-muted">
              <dt class="col-12" v-if="entry.employeeMessage">{{ entry.reward.customMessageLabel || $t('rewards.labels.employeeMessage') }}</dt>
              <dd class="col-12" v-if="entry.employeeMessage"><p class="m-0">{{ entry.employeeMessage }}</p></dd>
              <dt class="col-12" v-if="entry.recruiterMessage">{{ $t('rewards.labels.recruiterMessage') }}</dt>
              <dd class="col-12" v-if="entry.recruiterMessage"><p class="m-0">{{ entry.recruiterMessage }}</p></dd>
              <dt class="col-sm-4">{{ $t('rewards.labels.tokenCost') }}</dt>
              <dd class="col-sm-8">{{ entry.tokenCost }} <rewards-coin/></dd>
              <dt class="col-sm-4">{{ $t('rewards.labels.createdAt') }}</dt>
              <dd class="col-sm-8">{{ entry.createdAt | timeAgo }}</dd>
              <dt class="col-sm-4">{{ $t('rewards.labels.updatedAt') }}</dt>
              <dd class="col-sm-8">{{ entry.updatedAt | timeAgo }}</dd>
            </dl>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <template v-if="Array.isArray(rewards) && rewards.length">
      <h3>{{ $t('rewards.reward-shop-title') }}</h3>
      <b-row class="my-3">
        <b-col class="mb-4" cols="12" sm="6" v-for="entry in rewards" :key="`reward-${entry.id}`">
          <b-card class="h-100" body-class="p-0">
            <b-media class="p-3">
              <template v-slot:aside v-if="entry.image && typeof entry.image.fileLocation === 'string'">
                <b-img v-if="entry.image.fileLocation.endsWith('.gif')" height="128" width="128"
                  :src="entry.image.fileLocation" :alt="entry.title"/>
                <b-img v-else height="128" width="128"
                  :src="entry.image.fileLocation | cdn('resize', 128, 128)" :alt="entry.title"/>
              </template>
              <template v-slot:aside v-else>
                <b-img blank blank-color="#ccc" height="128" width="128"/>
              </template>
              <b-card-body class="py-2 px-3">
                <b-card-title>{{ entry.title }}</b-card-title>
                <b-card-text>{{ entry.description }}</b-card-text>
              </b-card-body>

              <b-card-footer class="bg-white border-0 d-flex justify-content-end align-items-center">
                <b-btn v-if="entry.url" :href="entry.url" target="_blank" variant="outline-primary" class="mr-3">
                  {{ $t('ctas.learn-more') }} <font-awesome-icon icon="external-link-alt" size="sm"/>
                </b-btn>
                <b-btn @click.prevent="() => selectedClaimID = entry.id" variant="primary"
                  :disabled="(canClaimRewards && canClaimThisReward(entry)) === false">
                    {{ entry.tokenCost }} <rewards-coin/>
                </b-btn>
              </b-card-footer>
            </b-media>
            <b-card-footer class="d-flex align-items-center" v-if="isRecruiterAdmin">
              <b-btn @click.prevent="() => selectedEditID = entry.id" variant="link">{{ $t('ctas.pencil-edit') }}</b-btn>
              <div class="flex-fill"/>
              <i18n path="general.added" tag="span">
                <template v-slot:timeAgo>{{ entry.createdAt | timeAgo }}</template>
              </i18n>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <template v-if="canClaimRewards && Array.isArray(afterClaimedRewards) && afterClaimedRewards.length">
      <h3>{{ $t('rewards.other-rewards-title') }}</h3>
      <b-row class="mt-3">
        <b-col class="mb-3" cols="12" sm="6" v-for="entry in afterClaimedRewards" :key="`reward-${entry.id}`">
          <b-card class="h-100">
            <div class="d-flex flex-row justify-content-start align-items-center mb-3">
              <span v-if="entry.reward">{{ entry.reward.title }}</span>
              <div class="flex-fill"/>
              <strong v-if="entry.status === 'REQUESTED' || entry.status === 'VERIFIED'">{{ $t('rewards.statuses.REQUESTED') }}</strong>
              <strong v-if="entry.status === 'APPROVED'">{{ $t('rewards.statuses.APPROVED') }}</strong>
              <strong v-if="entry.status === 'FULFILLED'">{{ $t('rewards.statuses.FULFILLED') }}</strong>
              <strong v-if="entry.status === 'REJECTED'">{{ $t('rewards.statuses.REJECTED') }}</strong>
            </div>
            <dl class="row text-muted">
              <dt class="col-12" v-if="entry.employeeMessage">{{ entry.reward.customMessageLabel || $t('rewards.labels.employeeMessage') }}</dt>
              <dd class="col-12" v-if="entry.employeeMessage"><p class="m-0">{{ entry.employeeMessage }}</p></dd>
              <dt class="col-12" v-if="entry.recruiterMessage">{{ $t('rewards.labels.recruiterMessage') }}</dt>
              <dd class="col-12" v-if="entry.recruiterMessage"><p class="m-0">{{ entry.recruiterMessage }}</p></dd>
              <dt class="col-sm-4">{{ $t('rewards.labels.tokenCost') }}</dt>
              <dd class="col-sm-8">{{ entry.tokenCost }} <rewards-coin/></dd>
              <dt class="col-sm-4">{{ $t('rewards.labels.createdAt') }}</dt>
              <dd class="col-sm-8">{{ entry.createdAt | timeAgo }}</dd>
              <dt class="col-sm-4">{{ $t('rewards.labels.updatedAt') }}</dt>
              <dd class="col-sm-8">{{ entry.updatedAt | timeAgo }}</dd>
            </dl>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <div class="d-flex flex-row justify-content-center">
      <p><small>{{ $t('rewards.footer-text') }}</small></p>
    </div>

    <!-- Claim Reward Modal -->
    <b-modal v-if="selectedClaimID && selectedClaimEntry" visible size="md" hide-header-close hide-footer lazy
      @hidden="() => selectedClaimID = null" :title="$t('rewards.claim-reward-modal.title', { rewardTitle: selectedClaimEntry.title })">
        <b-form-group :label="selectedClaimEntry.customMessageLabel || $t('rewards.claim-reward-modal.customMessageLabel')" label-for="employeeMessage">
          <b-form-textarea id="employeeMessage" v-model="claimEmployeeMessage" rows="3" max-rows="6"
            :placeholder="selectedClaimEntry.customMessageRequired ? 'Required' : 'Optional'"/>
        </b-form-group>

        <p>Confirm below to request your reward.
          Once confirmed your claim will be verified &amp; an administrator will approve the reward.
          We will notify you when your claim is approved &amp; your reward will be sent to you by email or post, if applicable.</p>

        <b-alert v-if="!canClaimRewards" class="px-2" show variant="warning">
          {{ $t('rewards.errors.recruiters-cannot-claim') }}
        </b-alert>

        <div class="bg-white border-0 d-flex justify-content-start align-items-center">
          <b-btn class="mr-3" :disabled="isClaimEntryActionDisabled" @click.prevent="claimReward" variant="primary">
            {{ $t('rewards.ctas.claim-for-tokens', { tokenCost: selectedClaimEntry.tokenCost }) }} <rewards-coin/>
          </b-btn>
          <b-btn @click.prevent="() => selectedClaimID = null" variant="outline-danger">
            {{ $t('ctas.cancel') }}
          </b-btn>
        </div>
    </b-modal>

    <!-- Edit Modal -->
    <b-modal v-if="isRecruiterAdmin" :visible="Boolean(selectedEditID)" size="md" hide-footer no-close-on-backdrop
      lazy @hidden="() => selectedEditID = null" :title="selectedEditID === 'NEW' ? 'Create Reward' : 'Edit Reward'">
        <b-form v-if="form">
          <b-form-group :label="$t('rewards.labels.title')" label-for="title" label-align-sm="right" label-cols-sm="3">
            <b-input id="title" placeholder="Title" v-model="form.title"/>
            <b-form-text>{{ $t('general.required') }}</b-form-text>
          </b-form-group>
          <b-form-group :label="$t('rewards.labels.description')" label-for="description" label-align-sm="right" label-cols-sm="3">
            <b-textarea id="description" no-auto-shrink no-resize rows="3" max-rows="3"
              placeholder="Set a useful description" v-model="form.description"/>
          </b-form-group>
          <b-form-group :label="$t('rewards.labels.image')" label-for="image" label-align-sm="right" label-cols-sm="3">
            <asset-block :field="{ type: 'IMAGE' }" id="image" show-preview v-model="form.image"/>
          </b-form-group>
          <b-form-group :label="$t('rewards.labels.url')" label-for="url" label-align-sm="right" label-cols-sm="3">
            <b-input id="url" placeholder="E.g. https://..." v-model="form.url"/>
          </b-form-group>
          <hr/>
          <b-form-group :label="$t('rewards.labels.tokenCost')" label-for="tokenCost" label-align-sm="right" label-cols-sm="3">
            <b-input-group>
              <template v-slot:prepend><div class="input-group-text"><rewards-coin/></div></template>
              <b-input id="tokenCost" number :min="1" type="number" v-model="form.tokenCost"/>
            </b-input-group>
            <b-form-text>{{ $t('rewards.labels.tokenCost-help') }}</b-form-text>
          </b-form-group>
          <b-form-group :label="$t('rewards.labels.internalCost')" label-for="internalCost" label-align-sm="right" label-cols-sm="3">
            <b-input-group :prepend="internalCostCurrency">
              <b-input id="internalCost" number :min="0" :max="999.99" step="0.01" type="number" v-model="form.internalCost"/>
            </b-input-group>
            <b-form-text>{{ $t('rewards.labels.internalCost-help') }}</b-form-text>
          </b-form-group>
          <b-form-group :label="$t('rewards.labels.totalStock')" label-for="totalStock" label-align-sm="right" label-cols-sm="3">
            <b-input-group prepend="×"><b-input id="totalStock" number :min="0" type="number" v-model="form.totalStock"/></b-input-group>
            <b-form-text>{{ $t('rewards.labels.totalStock-help') }}</b-form-text>
          </b-form-group>
          <b-form-group :label="$t('rewards.labels.availablity')" label-align-sm="right" label-cols-sm="3">
            <date-picker class="mb-2" format="YYYY-MM-DD hh:mm:ss" lang="en" placeholder="Available From" type="datetime" v-model="form.availableFrom"/>
            <date-picker format="YYYY-MM-DD hh:mm:ss" lang="en" placeholder="Available Until" type="datetime" v-model="form.availableUntil"/>
            <b-form-text>{{ $t('rewards.labels.availablity-help') }}</b-form-text>
          </b-form-group>
          <b-form-group :label="$t('rewards.labels.oneTimeClaim')" label-for="oneTimeClaim" label-align-sm="right" label-cols-sm="3">
            <b-form-checkbox class="pt-sm-1" id="oneTimeClaim" plain v-model="form.oneTimeClaim" :value="true" :unchecked-value="false">
              {{ $t('rewards.labels.oneTimeClaim-help') }}
            </b-form-checkbox>
            <b-form-text>{{ $t('rewards.labels.oneTimeClaim-useful') }}</b-form-text>
          </b-form-group>
          <hr/>
          <b-form-group :label="$t('rewards.labels.customMessageLabel')" label-for="customMessageLabel" label-align-sm="right" label-cols-sm="3">
            <b-input id="customMessageLabel" placeholder="Message" v-model="form.customMessageLabel"/>
            <b-form-text>{{ $t('rewards.labels.customMessage-help') }}</b-form-text>
          </b-form-group>
          <b-form-group label="" label-align-sm="right" label-cols-sm="3">
            <b-form-checkbox id="customMessageRequired" plain v-model="form.customMessageRequired"
              :value="true" :unchecked-value="false">
                {{ $t('rewards.labels.customMessage-required') }}
            </b-form-checkbox>
          </b-form-group>
          <hr/>
          <b-form-group label="Status" label-for="url" label-align-sm="right" label-cols-sm="3">
            <b-form-select :options="rewardStatusOptions" v-model="form.status"/>
          </b-form-group>
          <b-form-group label-cols-sm="3">
            <div class="d-flex flex-row">
              <b-btn class="mr-2" @click="saveReward" variant="primary">
                <template v-if="selectedEditID === 'NEW'">{{ $t('ctas.create-new') }}</template>
                <template v-else>{{ $t('ctas.save-changes') }}</template>
              </b-btn>
              <b-btn variant="outline-danger" @click="() => selectedEditID = null">
                <template v-if="selectedEditID === 'NEW'">{{ $t('ctas.cancel') }}</template>
                <template v-else>{{ $t('ctas.discard-changes') }}</template>
              </b-btn>
            </div>
          </b-form-group>
        </b-form>
    </b-modal>

    <!-- Manage Claims Modal -->
    <b-modal v-if="isRecruiterAdmin" id="recruiterList" size="xl" body-class="p-0" lazy hide-footer>
      <template v-slot:modal-header="{ close }">
        <div v-if="Array.isArray(selectedClaimedRewards) && selectedClaimedRewards.length" class="d-flex flex-row">
          <h5 class="modal-title">{{ $tc('manage.items-selected', selectedClaimedRewards.length) }}:</h5>
          <b-btn class="ml-2 py-0" @click="() => updateManyClaimedRewards({ status: 'VERIFIED' })" variant="warning">
            {{ $t('rewards.statuses.verify') }}
          </b-btn>
          <b-btn class="ml-2 py-0" @click="() => updateManyClaimedRewards({ status: 'APPROVED' })" variant="success">
            {{ $t('rewards.statuses.approve') }}
          </b-btn>
          <b-btn class="ml-2 py-0" @click="() => updateManyClaimedRewards({ status: 'FULFILLED' })" variant="outline-success">
            {{ $t('rewards.statuses.fulfill') }}
          </b-btn>
          <b-btn class="ml-2 py-0" @click="() => updateManyClaimedRewards({ status: 'REJECTED' })" variant="danger">
            {{ $t('rewards.statuses.reject') }}
          </b-btn>
          <b-btn class="ml-2 py-0" @click="() => updateManyClaimedRewards({ status: 'DELETED' })" variant="outline-danger">
            {{ $t('rewards.statuses.delete') }}
          </b-btn>
        </div>
        <h5 class="modal-title" v-else>{{ $t('rewards.manage-claimed-title') }}</h5>
        <b-btn-close @click="close"/>
      </template>
      <b-table hover striped show-empty responsive :fields="manageClaimFields" :items="manageClaimedRewards" per-page="100">
        <template #cell(id)="{ value: id }">
          <b-checkbox :checked="selectedClaimedRewards.includes(id)" @input="v => selectClaimedReward(id, v)"/>
        </template>

        <template #cell(reward.title)="{ value, item, toggleDetails }">
          {{ value }}
          <span v-if="item.message" @click="toggleDetails">
            <font-awesome-icon icon="comments" class="ml-2 text-muted"/>
          </span>
        </template>

        <template #cell(createdAt)="{ value }">{{ value | formatDateRelative }}</template>
        <template #cell(updatedAt)="{ value }">{{ value | formatDateRelative }}</template>

        <template #cell(opts)="row">
          <div class="table-action-buttons">
            <b-button class="text-muted py-0" size="sm" @click="row.toggleDetails" variant="link">
              <font-awesome-icon v-if="!row.detailsShowing" icon="chevron-down"/>
              <font-awesome-icon v-else icon="chevron-up"/>
            </b-button>
          </div>
        </template>

        <template #row-details="{ item }">
          <b-card>
            <b-row>
              <b-col cols="12" md="8">
                <dl class="row text-muted">
                  <dt class="col-sm-3">{{ $t('users.labels.emailAddress') }}</dt>
                  <dd class="col-sm-9"><a class="text-muted" :href="`mailto:${item.user.emailAddress}`">{{ item.user.emailAddress }}</a></dd>

                  <dt class="col-sm-3">{{ $t('rewards.labels.tokenCost') }}</dt>
                  <dd class="col-sm-9">{{ item.tokenCost }} <rewards-coin/></dd>

                  <dt class="col-12">{{ item.reward.customMessageLabel || $t('rewards.labels.employeeMessage') }}</dt>
                  <dd class="col-12">
                    <content-editable type="STRING" v-model="item.employeeMessage"
                      :placeholder="item.reward.customMessageRequired ? '(Required)' : '(Optional)'"
                      @saved="employeeMessage => updateClaimedReward(item.id, { employeeMessage })"/>
                  </dd>
                  <dt class="col-12">{{ $t('rewards.labels.recruiterMessage') }}</dt>
                  <dd class="col-12">
                    <content-editable type="STRING" placeholder="(Optional)" v-model="item.recruiterMessage"
                      @saved="recruiterMessage => updateClaimedReward(item.id, { recruiterMessage })"/>
                  </dd>

                  <dt class="col-sm-3 text-truncate">{{ $t('rewards.labels.createdAt') }}</dt>
                  <dd class="col-sm-9">{{ item.createdAt | formatDate('YYYY-MM-DD HH:mm') }}</dd>
                  <dt class="col-sm-3 text-truncate">{{ $t('rewards.labels.updatedAt') }}</dt>
                  <dd class="col-sm-9">{{ item.updatedAt | formatDate('YYYY-MM-DD HH:mm') }}</dd>
                </dl>
              </b-col>
              <b-col cols="12" md="4" class="d-flex flex-column align-items-end reward-detail-actions">
                <div class="d-flex flex-row" v-if="manageClaimStatuses && Array.isArray(manageClaimStatuses[item.status])">
                  <b-button class="flex-fill" :disabled="manageClaimStatuses[item.status].find(d => d && d.default).disabled"
                    @click="() => updateClaimedReward(item.id, { status: manageClaimStatuses[item.status].find(d => d && d.default).status })"
                    :variant="manageClaimStatuses[item.status].find(d => d && d.default).variant || 'outline-primary'"
                    >{{ manageClaimStatuses[item.status].find(d => d && d.default).label }}</b-button>
                  <b-dropdown v-if="manageClaimStatuses && Array.isArray(manageClaimStatuses[item.status])"
                    toggle-class="border-left-0" right variant="outline-primary">
                      <b-dropdown-item v-for="({ label, status, default: isDefault, disabled, variant }) in manageClaimStatuses[item.status]"
                        :key="`${item.id}-set-${status}`" @click="() => updateClaimedReward(item.id, { status })"
                        :disabled="disabled" :variant="variant">
                          {{ label }}
                      </b-dropdown-item>
                  </b-dropdown>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </template>
        <template #empty>
          <h4 align="center">{{ $t('rewards.errors.no-rewards-claimed-yet') }}</h4>
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import _startCase from 'lodash/startCase';
import gql from 'graphql-tag';
import ContentEditable from '@src/components/misc/ContentEditable';
import RewardDetailsFragment from '@src/graphql/fragments/RewardDetails.gql';
import distanceInWords from 'date-fns/distance_in_words';
import { RewardsCoin } from '@src/components/Icons';
import { mapRoot } from '@src/mixins/root';

const RewardManageDetailsFragment = gql`
  fragment RewardManageDetails on Reward {
    customMessageLabel
    customMessageRequired
    internalCost
    availableStock
    totalStock
    availableFrom
    availableUntil
  }
`;

const UserClaimedRewardDetailsFragment = gql`
  fragment UserClaimedRewardDetails on ClaimedRewardEdge {
    id
    reward {
      id
      title
      customMessageLabel
      customMessageRequired
    }
    tokenCost
    employeeMessage
    recruiterMessage
    createdAt
    updatedAt
    status
  }
`;

const ListRewardsQuery = gql`
  query RewardsIndex($where: RewardWhereManyInput, $isRecruiter: Boolean = false) {
    company {
      id
      rewardScheme
      preferences {
        canRecruitersClaimRewards
      }

      rewards(where: $where, order: TOKENCOST_ASC) {
        ...RewardDetails
        ...RewardManageDetails
      }

      claimedRewards(where: { NOT: { status: DELETED } }, order: CREATED_DESC) @include(if: $isRecruiter) {
        id
        reward {
          id
          title
          customMessageLabel
          status
        }
        user {
          id
          fullName
          emailAddress
          status
        }
        tokenCost
        employeeMessage
        recruiterMessage
        createdAt
        updatedAt
        status
      }
    }
    user {
      id
      rewardTokensBalance
      rewardTokensTotal

      claimedRewards(order: CREATED_DESC) {
        ...UserClaimedRewardDetails
      }

      rewardTokensHistory {
        id
        eventName
        job {
          id
          title
          status
        }
        application {
          id
          fullName
          job {
            id
            title
            status
          }
          status
        }
        tokenCost
        createdAt
      }
    }
  }
  ${RewardDetailsFragment}
  ${RewardManageDetailsFragment}
  ${UserClaimedRewardDetailsFragment}
`;

const CreateRewardMutation = gql`
  mutation CreateReward($data: CreateRewardInput!) {
    createReward(create: $data) {
      ...RewardDetails
      ...RewardManageDetails
    }
  }
  ${RewardDetailsFragment}
  ${RewardManageDetailsFragment}
`;

const UpdateRewardMutation = gql`
  mutation UpdateReward($id: ID!, $data: UpdateRewardInput!) {
    updateReward(update: $data, where: { id: $id }) {
      ...RewardDetails
      ...RewardManageDetails
    }
  }
  ${RewardDetailsFragment}
  ${RewardManageDetailsFragment}
`;

export default {
  components: {
    AssetBlock: () => import('@/components/settings/AssetBlock'),
    ContentEditable,
    ClaimedDescription: () => import('@src/components/rewards/ClaimedDescription'),
    DatePicker: () => import('vue2-datepicker'),
    RewardsProgressBar: () => import('@/components/rewards/Progress'),
    RewardsCoin,
  },
  data: () => ({
    err: null,
    form: null,
    canClaimRewards: false,
    selectedClaimID: null,
    selectedEditID: null,
    rewardTokensBalance: null,
    rewardTokensTotal: null,
    claimEmployeeMessage: null,
    outstandingRewards: null,
    claimedRewards: null,
    rewardScheme: null,
    rewardTokensHistory: null,
    selectedClaimedRewards: [],
    manageClaimedRewards: null,
  }),
  metaInfo() {
    const meta = {
      title: this.$t('rewards.title'),
    };

    if (this.selectedEditID) {
      meta.title = this.$t(this.selectedEditID === 'NEW' ? 'rewards.ctas.create-reward' : 'rewards.ctas.update-reward');
    }

    return meta;
  },
  created() {
    const { sitePreferences } = this.company || {};
    if (sitePreferences && sitePreferences.isRewardIntegrationEnabled) {
      this.$router.push({ name: 'RewardsBrowser' });
    }
  },
  apollo: {
    rewards: {
      query: ListRewardsQuery,
      variables() {
        const where = {
          isAvailableNow: this.isRecruiterAdmin ? null : true,
          status: 'ACTIVE',
        };

        return { where, isRecruiter: this.isRecruiter };
      },
      update: ({ company: { rewards } }) => rewards || [],
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { rewardScheme } = data.company;
          this.rewardScheme = rewardScheme ? { ...rewardScheme } : null;

          if (this.isRecruiter) {
            const { claimedRewards, preferences } = data.company;
            this.manageClaimedRewards = Array.isArray(claimedRewards) ? claimedRewards : [];

            const { canRecruitersClaimRewards } = preferences || {};
            this.canClaimRewards = canRecruitersClaimRewards === true;
          } else {
            this.canClaimRewards = true;
          }

          if (data.user) {
            const { rewardTokensBalance, rewardTokensTotal, outstandingRewards, claimedRewards, rewardTokensHistory } = data.user || {};

            this.rewardTokensBalance = parseInt(rewardTokensBalance, 10) || 0;
            this.rewardTokensTotal = parseInt(rewardTokensTotal, 10) || 0;
            this.outstandingRewards = Array.isArray(outstandingRewards) ? outstandingRewards : [];
            this.claimedRewards = Array.isArray(claimedRewards) ? claimedRewards : [];
            this.rewardTokensHistory = Array.isArray(rewardTokensHistory) ? rewardTokensHistory : [];
          }
        }
      },
      error(err) {
        this.err = err;
      },
    },
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isRecruiter', 'isRecruiterAdmin', 'isEnglishLang' ]),
    internalCostCurrency() {
      const { handle } = this.company || {};
      return handle === 'optiver' ? '€' : '£';
    },
    selectedClaimEntry() {
      return (Array.isArray(this.rewards) && this.selectedClaimID)
        ? this.rewards.find(r => r && r.id === this.selectedClaimID)
        : null;
    },
    isClaimEntryActionDisabled() {
      if (this.canClaimRewards && this.selectedClaimEntry) {
        const { customMessageRequired } = this.selectedClaimEntry;
        const { claimEmployeeMessage } = this;
        return customMessageRequired && !claimEmployeeMessage;
      } else {
        return true;
      }
    },
    selectedEditEntry() {
      return (Array.isArray(this.rewards) && this.selectedEditID)
        ? this.rewards.find(r => r && r.id === this.selectedEditID)
        : null;
    },
    rewardStatusOptions: () => ({
      ACTIVE: 'Active',
      INACTIVE: 'Disabled',
      DELETED: 'Deleted',
    }),
    rewardSchemeCounts() {
      const { rewardTokensHistory: history } = this;
      return Object.keys(this.rewardScheme || {}).reduce((r, key) => {
        const claims = Array.isArray(history) ? history.filter(({ eventName }) => key === eventName) : [];
        return { ...r, [key]: claims.length };
      }, []);
    },
    beforeClaimedRewards() {
      return (Array.isArray(this.claimedRewards) ? this.claimedRewards : [])
        .filter(claimed => claimed && [ 'REQUESTED', 'VERIFIED', 'APPROVED' ].includes(claimed.status));
    },
    afterClaimedRewards() {
      return (Array.isArray(this.claimedRewards) ? this.claimedRewards : [])
        .filter(claimed => claimed && [ 'FULFILLED', 'REJECTED' ].includes(claimed.status));
    },
    manageClaimFields() {
      return [
        { key: 'id', label: '', thStyle: 'width:20px;' },
        { key: 'reward.title', label: 'Reward' },
        { key: 'user.fullName', label: 'User' },
        { key: 'createdAt', label: 'Claimed At' },
        { key: 'updatedAt' },
        { key: 'status', formatter: value => _startCase(`${value}`.toLowerCase()) },
        { key: 'opts', label: '', thStyle: 'width:20px;' },
      ];
    },
    manageClaimStatuses() {
      return {
        REQUESTED: [
          { label: this.$t('rewards.status-changes.REQUESTED.current'), status: 'REQUESTED', disabled: true },
          { label: this.$t('rewards.status-changes.VERIFIED.forward'), status: 'VERIFIED', default: true, variant: 'success' },
          { label: this.$t('rewards.status-changes.APPROVED.forward'), status: 'APPROVED' },
          { label: this.$t('rewards.status-changes.FULFILLED.forward'), status: 'FULFILLED' },
          { label: this.$t('rewards.status-changes.REJECTED.forward'), status: 'REJECTED', variant: 'danger' },
        ],
        VERIFIED: [
          { label: this.$t('rewards.status-changes.REQUESTED.back'), status: 'REQUESTED' },
          { label: this.$t('rewards.status-changes.VERIFIED.current'), status: 'VERIFIED', disabled: true },
          { label: this.$t('rewards.status-changes.APPROVED.forward'), status: 'APPROVED', default: true, variant: 'success' },
          { label: this.$t('rewards.status-changes.FULFILLED.forward'), status: 'FULFILLED' },
          { label: this.$t('rewards.status-changes.REJECTED.forward'), status: 'REJECTED', variant: 'danger' },
        ],
        APPROVED: [
          { label: this.$t('rewards.status-changes.REQUESTED.back'), status: 'REQUESTED' },
          { label: this.$t('rewards.status-changes.VERIFIED.back'), status: 'VERIFIED' },
          { label: this.$t('rewards.status-changes.APPROVED.current'), status: 'APPROVED', disabled: true },
          { label: this.$t('rewards.status-changes.FULFILLED.forward'), status: 'FULFILLED', default: true, variant: 'success' },
          { label: this.$t('rewards.status-changes.REJECTED.forward'), status: 'REJECTED', variant: 'danger' },
        ],
        FULFILLED: [
          { label: this.$t('rewards.status-changes.REQUESTED.back'), status: 'REQUESTED' },
          { label: this.$t('rewards.status-changes.VERIFIED.back'), status: 'VERIFIED' },
          { label: this.$t('rewards.status-changes.APPROVED.back'), status: 'APPROVED' },
          { label: this.$t('rewards.status-changes.FULFILLED.current'), status: 'FULFILLED', default: true, disabled: true, variant: 'outline-primary' },
          { label: this.$t('rewards.status-changes.REJECTED.forward'), status: 'REJECTED', variant: 'danger' },
        ],
        REJECTED: [
          { label: this.$t('rewards.status-changes.REQUESTED.back'), status: 'REQUESTED', variant: 'success' },
          { label: this.$t('rewards.status-changes.VERIFIED.back'), status: 'VERIFIED' },
          { label: this.$t('rewards.status-changes.APPROVED.back'), status: 'APPROVED' },
          { label: this.$t('rewards.status-changes.FULFILLED.forward'), status: 'FULFILLED', variant: 'warning' },
          { label: 'Rejected', status: 'REJECTED', default: true, disabled: true, variant: 'outline-danger' },
        ],
      };
    },
  },
  methods: {
    async saveReward() {
      try {
        const data = JSON.parse(JSON.stringify(this.form));
        this.$log.debug(data);

        const { selectedEditID } = this;
        const { title, tokenCost, totalStock } = data;
        this.$assert(typeof title === 'string' && title.length, new Error('Missing required Title'));
        this.$assert(typeof tokenCost === 'number' && tokenCost > 0, new Error('Missing required Token Cost'));
        this.$assert(typeof totalStock === 'number', new Error('Missing required Available Stock'));

        data.image = data.image && data.image.id ? { id: data.image.id } : null;

        const query = this.$options.apollo.rewards.query;
        const variables = this.$options.apollo.rewards.variables.call(this);

        await this.$apollo.mutate({
          mutation: selectedEditID === 'NEW' ? CreateRewardMutation : UpdateRewardMutation,
          variables: { id: selectedEditID === 'NEW' ? undefined : selectedEditID, data },
          update(store, { data: { [selectedEditID === 'NEW' ? 'createReward' : 'updateReward']: reward } }) {
            const data = store.readQuery({ query, variables });

            if (data && data.company && Array.isArray(data.company.rewards)) {
              if (selectedEditID === 'NEW') {
                data.company.rewards.push(reward);
              } else {
                const i = data.company.rewards.findIndex(r => r && r.id === selectedEditID);
                data.company.rewards.splice(i, i >= 0 ? 1 : 0, reward);
              }
            }

            store.writeQuery({ query, variables, data });
          },
        });

        this.$notification({
          title: selectedEditID === 'NEW' ? 'Reward created' : 'Reward updated',
          message: selectedEditID === 'NEW' ? `Successfully created the ${title} reward` : `Successfully updated the ${title} reward`,
          variant: 'success',
        });

        this.selectedEditID = null;
      } catch (err) {
        this.$notifyErr(err);
      }
    },
    canClaimThisReward({ id: rewardID, tokenCost, oneTimeClaim }) {
      const { rewardTokensBalance, claimedRewards } = this;

      if (oneTimeClaim && Array.isArray(claimedRewards) && claimedRewards.find(c => c && c.reward && c.reward.id === rewardID)) {
        return false;
      }

      return rewardTokensBalance >= tokenCost;
    },
    async claimReward() {
      try {
        const { claimEmployeeMessage: employeeMessage, selectedClaimEntry: reward } = this;
        this.$assert(reward && reward.id, new Error('Reward not found'), { where: { id: this.selectedClaimID } });
        this.$assert(this.canClaimThisReward(reward), new Error('Sorry, you cannot claim this reward'));
        this.$assert(!reward.customMessageRequired || employeeMessage, new Error('You must enter a custom message'));

        const query = this.$options.apollo.rewards.query;
        const variables = this.$options.apollo.rewards.variables.call(this);

        const create = {
          reward: { id: this.selectedClaimID },
          user: { self: true },
          employeeMessage,
        };

        await this.$apollo.mutate({
          mutation: gql`
            mutation CreateClaimedReward($create: CreateClaimedRewardInput!) {
              createClaimedReward(create: $create) {
                ...UserClaimedRewardDetails
              }
            }
            ${UserClaimedRewardDetailsFragment}
          `,
          variables: { create },
          update(store, { data: { createClaimedReward: claimed } }) {
            const data = store.readQuery({ query, variables });

            if (data && data.user && Array.isArray(data.user.claimedRewards)) {
              data.user.claimedRewards.unshift(claimed);
            }

            store.writeQuery({ query, variables, data });
          },
        });

        this.selectedClaimID = null;
      } catch (err) {
        this.$notifyErr(err);
      }
    },
    async updateClaimedReward(rewardID, update) {
      try {
        this.$assert(this.isRecruiter, 'Only recruiters can perform this action');

        await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateClaimedReward($update: UpdateClaimedRewardInput!, $rewardID: ID!) {
              updateClaimedReward(update: $update, where: { id: $rewardID }) {
                id
                status
              }
            }
          `,
          variables: {
            update,
            rewardID,
          },
        });

        this.$notification({
          message: 'Claimed reward updated',
          variant: 'success',
        });
      } catch (err) {
        this.$notifyErr(err);
      }
    },
    selectClaimedReward(rewardID, checked) {
      const exists = this.selectedClaimedRewards.includes(rewardID);
      if (!exists && checked) {
        this.selectedClaimedRewards.push(rewardID);
      } else if (exists && !checked) {
        this.selectedClaimedRewards.splice(this.selectedClaimedRewards.indexOf(rewardID), 1);
      }
    },
    async updateManyClaimedRewards(update) {
      try {
        this.$assert(this.isRecruiter, 'Only recruiters can perform this action');
        this.$assert(this.selectedClaimedRewards.length, 'No items are selected');

        await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateClaimedReward($update: UpdateClaimedRewardInput!, $rewardIDs: [ ID! ]!) {
              updateManyClaimedRewards(update: $update, where: { id_in: $rewardIDs }) {
                id
                status
              }
            }
          `,
          variables: {
            update,
            rewardIDs: this.selectedClaimedRewards,
          },
        });

        this.$notification({
          message: 'Claimed rewards updated',
          variant: 'success',
        });
      } catch (err) {
        this.$notifyErr(err);
      }
    },
  },
  filters: {
    timeAgo(createdAt) {
      return distanceInWords(new Date(), createdAt, { addSuffix: true });
    },
  },
  watch: {
    selectedClaimID() {
      this.claimEmployeeMessage = null;
    },
    selectedEditID(id) {
      /* eslint-disable object-property-newline */
      if (id === 'NEW') {
        this.form = { tokenCost: 10, totalStock: 1, status: 'ACTIVE' };
      } else if (this.selectedEditEntry) {
        const {
          title, description, image, url, tokenCost, internalCost, totalStock, oneTimeClaim,
          availableFrom, availableUntil, customMessageLabel, customMessageRequired, status,
        } = this.selectedEditEntry;
        this.form = {
          title, description, image, url, tokenCost, internalCost, totalStock, oneTimeClaim,
          availableFrom, availableUntil, customMessageLabel, customMessageRequired, status,
        };
      } else {
        this.form = null;
      }
    },
  },
};
</script>

<style lang="scss">
.rewards-page {
  table {
    table-layout: fixed;

    th,td {
      text-align: center;
    }
  }
}

.modal#recruiterList {
  .reward-detail-actions {
    .dropdown > .btn:not(.dropdown-toggle) {
      width: 100%;
    }
  }
}

.modal-content {
  min-height: 60vh;

  table {
    min-height: 60vh;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/User/Rewards.vue